import type { ElementComponent } from '../render/elementRenderContext';
import { boolProp, textProp, type ElementProps } from '../render/elementProps';
import { FieldFrame, controlAttrs, useFieldBox } from './FieldFrame';
import { useFormField } from './useFormField';
import './forms.css';

/**
 * The two answers a visitor picks rather than types: one of a closed list, and a single yes.
 *
 * A dropdown submits the merchant's stored value and shows the merchant's label, which are two
 * different strings on purpose — a city renamed in the builder must not change what every order
 * already placed says it was delivered to.
 */

/** One choice a dropdown offers. */
type Choice = { value: string; label: string };

/** As many choices as one dropdown may hold; past this a merchant wants a search, not a list. */
const MAX_CHOICES = 200;

// Reads the merchant's option list, dropping every row that names no value a form could submit.
function choicesOf(props: ElementProps): readonly Choice[] {
  const rows = props.options;
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row): row is ElementProps => row !== null && typeof row === 'object' && !Array.isArray(row))
    .map((row) => ({ value: textProp(row, 'value'), label: textProp(row, 'label') }))
    .filter((choice) => choice.value !== '' || choice.label !== '')
    .map((choice) => ({ value: choice.value || choice.label, label: choice.label || choice.value }))
    .slice(0, MAX_CHOICES);
}

// Draws a closed list the visitor picks one of, with the merchant's own prompt sitting above the choices.
export const SelectElement: ElementComponent = ({ node }) => {
  const field = useFormField(node, { shape: 'none', empty: '' });
  const box = useFieldBox(node);
  const placeholder = textProp(node.props, 'placeholder', 'Please choose…');
  return (
    <FieldFrame
      id={field.rule.domId} label={field.rule.label} required={field.rule.required}
      error={field.error} errorId={field.errorId} marks={box.marks}
    >
      <select
        {...controlAttrs(field, placeholder)}
        className={box.className}
        dir="auto"
        value={String(field.value)}
        onChange={(event) => field.onChange(event.target.value)}
      >
        <option value="">{placeholder}</option>
        {choicesOf(node.props).map((choice) => (
          <option value={choice.value} key={choice.value}>{choice.label}</option>
        ))}
      </select>
    </FieldFrame>
  );
};

// Draws one box a visitor ticks, with its words inside the label so the words are part of the target.
export const CheckboxElement: ElementComponent = ({ node }) => {
  const field = useFormField(node, { shape: 'none', empty: boolProp(node.props, 'checked', false) });
  const box = useFieldBox(node);
  const words = textProp(node.props, 'label');
  return (
    <div className="xs-form-field" {...box.marks}>
      <label className={box.className ? `xs-form-check ${box.className}` : 'xs-form-check'} htmlFor={field.rule.domId}>
        <input
          {...controlAttrs(field, words)}
          className="xs-form-check-box"
          type="checkbox"
          checked={field.value === true}
          onChange={(event) => field.onChange(event.target.checked)}
        />
        <span dir="auto">{words}{field.rule.required ? ' *' : ''}</span>
      </label>
      {field.error === '' ? null : <span className="xs-form-error" id={field.errorId} dir="auto">{field.error}</span>}
    </div>
  );
};
